import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export const MOBILE_INVALID_START_MESSAGE = 'Mobile number cannot start with 0 to 5';
export const MOBILE_LENGTH_MESSAGE = 'Enter a valid 10-digit mobile number';
export const MOBILE_FULL_PATTERN = /^[6-9]\d{9}$/;

export function isInvalidMobileStart(value: unknown): boolean {
  const text = String(value ?? '').trim();
  return text.length > 0 && /^[0-5]/.test(text);
}

export function isValidIndianMobile(value: unknown): boolean {
  return MOBILE_FULL_PATTERN.test(String(value ?? '').trim());
}

/**
 * Detect phone-number fields (not OTP / PIN / account numbers).
 */
export function isMobilePhoneNumberField(el: HTMLInputElement | HTMLTextAreaElement): boolean {
  if (!(el instanceof HTMLInputElement)) {
    return false;
  }

  const explicit = (el.getAttribute('data-input-filter') || '').toLowerCase().trim();
  if (explicit === 'mobile') {
    return true;
  }
  if (explicit && explicit !== 'digits' && explicit !== 'number' && explicit !== 'numeric') {
    return false;
  }

  const formControlName = (el.getAttribute('formcontrolname') || '').toLowerCase();
  const meta = `${el.name || ''} ${el.id || ''} ${el.placeholder || ''} ${el.className || ''} ${formControlName}`.toLowerCase();

  if (/otp|one[-\s_]?time|verification\s*code|pincode|pin_?code|account_?number|aadhar|aadhaar/.test(meta)) {
    return false;
  }

  const maxLength = el.maxLength > 0 ? el.maxLength : 0;
  if (maxLength > 0 && maxLength < 10) {
    return false;
  }
  if (maxLength > 10) {
    return false;
  }

  if (
    /mobile|phone|phonenumber|mobilenumber|partnernumber|recipientmobile|familyclientmobile/.test(meta)
  ) {
    return true;
  }

  return false;
}

export interface MobileValidatorOptions {
  /** When true, empty value is invalid. Default false. */
  required?: boolean;
}

/**
 * Indian mobile validator:
 * - empty allowed unless required
 * - if filled: must start with 6-9 and be exactly 10 digits
 */
export function mobileNumberValidator(options: MobileValidatorOptions = {}): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = String(control.value ?? '').trim();
    if (!value) {
      return options.required ? { required: true } : null;
    }
    if (isInvalidMobileStart(value)) {
      return { invalidMobileStart: true };
    }
    if (!/^\d+$/.test(value)) {
      return { pattern: true };
    }
    if (value.length !== 10) {
      return { minlength: { requiredLength: 10, actualLength: value.length } };
    }
    if (!MOBILE_FULL_PATTERN.test(value)) {
      return { invalidMobileStart: true };
    }
    return null;
  };
}
